/**
 * Loan Pricing Service
 * Handles margin categorization, LO compensation tracking, and pricing calculations
 * for the Product & Pricing tab Net Revenue display.
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { lockCalendarLabel, withLockCalendarDates } from '@/services/lockCalendarDates';

const log = createComponentLogger('LoanPricingService', 'loan-pricing');

// ============================================================================
// Types
// ============================================================================

export interface LoanPricingAdjustment {
  id: string;
  encompassLoanId: string;
  organizationId: string;
  originalDescription: string;
  displayCategory: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
}

export interface LOUserInfo {
  id: string;
  fullName: string;
  email: string;
  bankedCompensation: number | null;
  /** OB-driven comp rate; an OB-only plan has this set with bankedCompensation null. */
  optimalBlueCurrentPercentage: number | null;
  encompassUserId: string | null;
}

// New flexible pricing items types
export type PricingItemType = 'margin' | 'lo_compensation' | 'lender_credit';
export type PricingAmountType = 'bps' | 'dollars';
export type PricingSource = 'encompass' | 'manual' | 'default';

export interface LoanPricingItem {
  id: string;
  encompassLoanId: string;
  organizationId: string;
  itemType: PricingItemType;
  description: string | null;
  amount: number;
  amountType: PricingAmountType;
  direction: 1 | -1; // +1 = revenue, -1 = deduction
  source: PricingSource;
  encompassFieldId: string | null;
  loanOfficerId: string | null;
  isDefaultRate: boolean;
  reductionBps: number; // LO comp reduction in basis points (0 for non-lo_compensation rows)
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

// Database row type for loan_pricing_items
interface PricingItemRow {
  id: string;
  encompass_loan_id: string;
  organization_id: string;
  item_type: PricingItemType;
  description: string | null;
  amount: number;
  amount_type: PricingAmountType;
  direction: number;
  source: PricingSource;
  encompass_field_id: string | null;
  loan_officer_id: string | null;
  is_default_rate: boolean;
  reduction_bps: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

// Database row types (snake_case)
interface PricingAdjustmentRow {
  id: string;
  encompass_loan_id: string;
  organization_id: string;
  original_description: string;
  display_category: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

// ============================================================================
// Conversion Utilities
// ============================================================================

/**
 * Convert basis points (e.g., 125 = 1.25%) to dollars
 */
export function bpsToDollars(bps: number, loanAmount: number): number {
  return (bps / 10000) * loanAmount;
}

/**
 * Convert dollars to basis points
 */
export function dollarsToBps(dollars: number, loanAmount: number): number {
  if (loanAmount === 0) {
return 0;
}
  return (dollars / loanAmount) * 10000;
}

/**
 * Format number as currency
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

/**
 * Format number with specific decimal places
 */
export function formatPoints(value: number, decimals: number = 4): string {
  return value.toFixed(decimals);
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get current user's organization ID from security context
 */
async function getCurrentUserOrganizationId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  // Try cache first
  const { data: context } = await supabase
    .from('auth_security_context')
    .select('organization_id')
    .eq('user_id', user.id)
    .maybeSingle();

  if (context?.organization_id) {
return context.organization_id;
}

  // Fallback: query users table directly (source of truth)
  const { data: userData, error: userError } = await supabase
    .from('users')
    .select('organization_id')
    .eq('id', user.id)
    .single();

  if (userError) {
    throw new Error(`Failed to fetch user organization: ${userError.message}`);
  }

  if (!userData?.organization_id) {
    throw new Error('User organization not found');
  }

  return userData.organization_id;
}

/**
 * Transform database row to camelCase interface
 */
function transformAdjustmentRow(row: PricingAdjustmentRow): LoanPricingAdjustment {
  return {
    id: row.id,
    encompassLoanId: row.encompass_loan_id,
    organizationId: row.organization_id,
    originalDescription: row.original_description,
    displayCategory: row.display_category,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    createdBy: row.created_by
  };
}

function transformPricingItemRow(row: PricingItemRow): LoanPricingItem {
  return {
    id: row.id,
    encompassLoanId: row.encompass_loan_id,
    organizationId: row.organization_id,
    itemType: row.item_type,
    description: row.description,
    amount: row.amount,
    amountType: row.amount_type,
    direction: row.direction as 1 | -1,
    source: row.source,
    encompassFieldId: row.encompass_field_id,
    loanOfficerId: row.loan_officer_id,
    isDefaultRate: row.is_default_rate,
    reductionBps: Number(row.reduction_bps) || 0,
    sortOrder: row.sort_order,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    createdBy: row.created_by,
    updatedBy: row.updated_by
  };
}

// ============================================================================
// Loan Pricing Items Functions (New flexible table)
// ============================================================================

/**
 * Get all pricing items for a loan
 */
export async function getLoanPricingItems(
  encompassLoanId: string
): Promise<LoanPricingItem[]> {
  log.debug('Fetching loan pricing items', { encompassLoanId });

  const { data, error } = await supabase
    .from('loan_pricing_items')
    .select('*')
    .eq('encompass_loan_id', encompassLoanId)
    .order('item_type')
    .order('sort_order');

  if (error) {
    log.error('Failed to fetch loan pricing items', { encompassLoanId }, error);
    throw error;
  }

  return (data || []).map(transformPricingItemRow);
}

// ============================================================================
// Pricing Adjustment Functions (Legacy - for margin category renaming)
// ============================================================================

/**
 * Get all pricing adjustments for a loan
 */
export async function getPricingAdjustments(
  encompassLoanId: string
): Promise<LoanPricingAdjustment[]> {
  log.debug('Fetching pricing adjustments', { encompassLoanId });

  const { data, error } = await supabase
    .from('loan_pricing_adjustments')
    .select('*')
    .eq('encompass_loan_id', encompassLoanId);

  if (error) {
    log.error('Failed to fetch pricing adjustments', { encompassLoanId }, error);
    throw error;
  }

  return (data || []).map(transformAdjustmentRow);
}

// ============================================================================
// LO User Lookup Functions
// ============================================================================

/**
 * Get all loan officers for organization (for dropdown)
 */
export async function getOrganizationLoanOfficers(): Promise<LOUserInfo[]> {
  log.debug('Fetching organization loan officers');

  const organizationId = await getCurrentUserOrganizationId();

  const { data, error } = await supabase
    .from('users')
    .select(`
      id,
      first_name,
      last_name,
      email,
      banked_compensation,
      optimal_blue_current_percentage,
      encompass_user_id
    `)
    .eq('organization_id', organizationId)
    .eq('is_loan_officer', true)
    .is('deleted_at', null)
    .order('last_name');

  if (error) {
    log.error('Failed to fetch loan officers', {}, error);
    throw error;
  }

  return (data || []).map(user => ({
    id: user.id,
    fullName: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
    email: user.email,
    bankedCompensation: user.banked_compensation,
    optimalBlueCurrentPercentage: user.optimal_blue_current_percentage ?? null,
    encompassUserId: user.encompass_user_id
  }));
}

/**
 * Get display category for an adjustment description
 * Returns custom category if saved, otherwise applies default renaming rules
 */
export function getDisplayCategory(
  originalDescription: string,
  savedAdjustments: LoanPricingAdjustment[]
): string {
  // Check for saved override
  const saved = savedAdjustments.find(
    adj => adj.originalDescription === originalDescription
  );
  if (saved) {
    return saved.displayCategory;
  }

  // Apply default renaming rules
  if (originalDescription.toLowerCase() === 'uncategorized') {
    return 'Lendwise Margin';
  }

  return originalDescription;
}

// ============================================================================
// Banker Recon Integration
// ============================================================================

/**
 * Get pricing items formatted for Banker Recon auto-population
 * Aggregates LO compensation, lender credits, and concessions from loan_pricing_items
 */
export async function getPricingItemsForRecon(
  encompassLoanId: string,
  loanAmount: number,
  encompassLoCompBps: number = 0
): Promise<{
  hasData: boolean;
  loCompensation: { type: 'flat_fee' | 'percentage'; value: number };
  loCompReduction: {
    baseLoCompBps: number;
    rawReductionBps: number;
    appliedReductionBps: number;
    effectiveLoCompBps: number;
    reductionAmount: number;
  } | null;
  lenderCreditTotal: number;
}> {
  log.debug('Fetching pricing items for Banker Recon', { encompassLoanId, loanAmount });

  const items = await getLoanPricingItems(encompassLoanId);

  if (items.length === 0) {
    return {
      hasData: false,
      loCompensation: { type: 'flat_fee', value: 0 },
      loCompReduction: null,
      lenderCreditTotal: 0
    };
  }

  // LO compensation: base comes from Encompass, reduction from local metadata
  const loCompMetadata = items.find(i => i.itemType === 'lo_compensation');
  const storedReductionBps = loCompMetadata?.reductionBps ?? 0;
  const appliedReductionBps = Math.min(Math.max(storedReductionBps, 0), encompassLoCompBps);
  const effectiveLoCompBps = encompassLoCompBps - appliedReductionBps;
  const loCompReduction = encompassLoCompBps > 0 && appliedReductionBps > 0
    ? {
      baseLoCompBps: encompassLoCompBps,
      rawReductionBps: storedReductionBps,
      appliedReductionBps,
      effectiveLoCompBps,
      reductionAmount: Math.round(((loanAmount * appliedReductionBps) / 10000 + Number.EPSILON) * 100) / 100
    }
    : null;

  // Always report as percentage since Encompass base is in bps/points
  const loCompType: 'flat_fee' | 'percentage' = encompassLoCompBps > 0 ? 'percentage' : 'flat_fee';
  const loCompValue = encompassLoCompBps > 0 ? effectiveLoCompBps / 100 : 0; // BPS to percent

  // Sum lender credits (convert to dollars)
  const lenderCredits = items.filter(i => i.itemType === 'lender_credit');
  let lenderCreditTotal = 0;
  for (const item of lenderCredits) {
    lenderCreditTotal += item.amountType === 'bps'
      ? bpsToDollars(item.amount, loanAmount)
      : item.amount;
  }

  log.info('Pricing items for Banker Recon', {
    encompassLoanId,
    loCompType,
    loCompValue,
    lenderCreditTotal
  });

  return {
    hasData: true,
    loCompensation: { type: loCompType, value: loCompValue },
    loCompReduction,
    lenderCreditTotal
  };
}

// ============================================================================
// Lock Snapshot History
// ============================================================================

export interface LockSnapshot {
  id: string;
  encompassLoanId: string;
  version: number;
  label: string;
  triggerReason: string;
  capturedAt: string;
  rateLockFields: Record<string, unknown>;
  materialLockPayload: Record<string, unknown>;
  materialLockHash: string | null;
  pricingItemsSnapshot: LoanPricingItem[];
  pricingContext: Record<string, unknown>;
}

export interface LockVersion {
  id: string;
  encompassLoanId: string;
  version: number;
  label: string;
  triggerReason: string;
  capturedAt: string;
  rateLockFields: Record<string, unknown>;
  materialLockPayload: Record<string, unknown>;
  materialLockHash: string | null;
  sourceLockSnapshotId: string | null;
  sourceLockSnapshotIds: string[];
}

/**
 * Get all pure Encompass/OB lock versions for a loan, newest first.
 *
 * Unlike legacy `lock_snapshots`, these rows do not carry local UI pricing
 * inputs. Product & Pricing lock history must use this model so discount,
 * BPC, lender credit, LOCR, concessions, etc. never seed or mutate the lock
 * version picker.
 */
export async function getLockVersions(
  encompassLoanId: string
): Promise<LockVersion[]> {
  const { data, error } = await supabase
    .from('lock_versions')
    .select('*')
    .eq('encompass_loan_id', encompassLoanId)
    .order('version', { ascending: false });

  if (error) {
    log.error('Failed to fetch lock versions', { encompassLoanId }, error);
    return [];
  }

  return (data || []).map(mapLockVersionRow);
}

export interface LockVersionRow {
  id: string;
  encompass_loan_id: string;
  version: number;
  label: string;
  trigger_reason: string;
  captured_at: string;
  rate_lock_fields: Record<string, unknown> | null;
  material_lock_payload: Record<string, unknown> | null;
  material_lock_hash: string | null;
  source_lock_snapshot_id: string | null;
  source_lock_snapshot_ids: string[] | null;
}

/**
 * One `lock_versions` row as a LockVersion. OB-written versions hold UTC
 * instants, so their lock dates and label are shown as Pacific calendar dates
 * (lockCalendarDates); the material payload and hash stay as stored.
 */
export function mapLockVersionRow(row: LockVersionRow): LockVersion {
  const rateLockFields = row.rate_lock_fields || {};
  return {
    id: row.id,
    encompassLoanId: row.encompass_loan_id,
    version: row.version,
    label: lockCalendarLabel(row.label, row.version, rateLockFields.rate_lock_buy_side_lock_date) ?? row.label,
    triggerReason: row.trigger_reason,
    capturedAt: row.captured_at,
    rateLockFields: withLockCalendarDates(rateLockFields),
    materialLockPayload: row.material_lock_payload || {},
    materialLockHash: row.material_lock_hash ?? null,
    sourceLockSnapshotId: row.source_lock_snapshot_id ?? null,
    sourceLockSnapshotIds: Array.isArray(row.source_lock_snapshot_ids)
      ? row.source_lock_snapshot_ids
      : []
  };
}

/**
 * Get all lock snapshots for a loan, newest first.
 * Transforms pricing_items_snapshot JSONB to camelCase LoanPricingItem[].
 */
export async function getLockSnapshots(
  encompassLoanId: string
): Promise<LockSnapshot[]> {
  const { data, error } = await supabase
    .from('lock_snapshots')
    .select('*')
    .eq('encompass_loan_id', encompassLoanId)
    .order('version', { ascending: false });

  if (error) {
    log.error('Failed to fetch lock snapshots', { encompassLoanId }, error);
    return [];
  }

  return (data || []).map(row => ({
    id: row.id,
    encompassLoanId: row.encompass_loan_id,
    version: row.version,
    label: row.label,
    triggerReason: row.trigger_reason,
    capturedAt: row.captured_at,
    rateLockFields: row.rate_lock_fields || {},
    materialLockPayload: row.material_lock_payload || {},
    materialLockHash: row.material_lock_hash ?? null,
    pricingItemsSnapshot: (row.pricing_items_snapshot || []).map((item: PricingItemRow) =>
      transformPricingItemRow(item)
    ),
    pricingContext: row.pricing_context || {}
  }));
}

/**
 * Compute the DB-owned material lock hash for a raw live loan/lock payload.
 * The client builds no identity policy; it passes raw fields to the database
 * normalizer and consumes the persisted/hash-compatible result.
 */
export async function computeMaterialLockHash(
  rateLockFields: Record<string, unknown>
): Promise<string | null> {
  const { data, error } = await supabase.rpc('material_lock_hash', {
    p_rate_lock_fields: rateLockFields
  });

  if (error) {
    log.warn('Failed to compute material lock hash', { error: error.message });
    return null;
  }
  return typeof data === 'string' ? data : null;
}

// ============================================================================
// Pricing Change History
// ============================================================================

const INVESTOR_LOAN_NUMBER_FIELD_ID = '352';

const PRICING_FIELD_LABELS: Record<string, string> = {
  'NEWHUD.X1150': 'Discount Points',
  'NEWHUD.X1151': 'Discount Point Amount',
  388: 'Borrower Paid Compensation',
  4794: 'Lender Credit',
  'LW.LO_REDUCTION': 'LO Comp Reduction',
  'LW.CONCESSION': 'Concession',
  352: 'Investor Loan #'
};

/**
 * Resolve the display label for a pricing audit row.
 *
 * Canonical mapping wins over whatever label was stored on the row.
 * This corrects drift from writers that stored `encompass_writable_fields.field_label`
 * (e.g. "Discount Point %") without a data backfill — Pricing Change History
 * now shows "Discount Points" consistently whether the row was written by the
 * observed-pricing webhook branch or the UI save path.
 *
 * Priority: canonical map → stored label → bare field_id as a last resort.
 */
export function resolvePricingFieldLabel(fieldId: string, storedLabel: string | null): string {
  return PRICING_FIELD_LABELS[fieldId] || storedLabel || fieldId;
}

export function formatPricingValue(raw: string | null, fieldId: string): string {
  // Pre-formatted fields: pass through as-is (no numeric parsing)
  if (fieldId === 'LW.CONCESSION') {
return raw || '—';
}
  // The Investor Loan # is text: letters and leading zeros are the value.
  if (fieldId === INVESTOR_LOAN_NUMBER_FIELD_ID) {
    return raw === null || raw.trim() === '' ? 'Blank' : raw;
  }
  // Canonical storage rule: null/'' means Blank (distinct from explicit 0,
  // which is stored as '0'). See supabase/functions/_shared/pricing-change-policy.ts.
  if (raw === null || raw === '') {
return 'Blank';
}
  // NEWHUD.X1151 and 4794 are stored as dollars; render with $ prefix.
  if (fieldId === 'NEWHUD.X1151' || fieldId === '4794') {
    const num = Number(raw);
    if (!Number.isFinite(num)) {
return raw;
}
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  }
  const suffix = fieldId === 'LW.LO_REDUCTION' ? ' bps' : '%';
  const num = Number(raw);
  if (!Number.isFinite(num)) {
return `${raw}${suffix}`;
}
  return `${num}${suffix}`;
}

// ============================================================================
// PRICING CHANGE HISTORY (grouped — Phase 2 Round 2B.12)
// ============================================================================

export type GroupedHistoryStatus =
  | 'pending'
  | 'pushing'
  | 'waiting_unlock'
  | 'waiting_retry'
  | 'validated'
  | 'cancelled'
  | 'failed'
  | 'legacy';

export interface GroupedHistoryChange {
  fieldId: string;
  fieldLabel: string;
  previousValue: string | null;
  newValue: string | null;
}

export interface GroupedHistoryEntry {
  /** pricing_write_groups.id for real groups; null for legacy pseudo-groups. */
  groupId: string | null;
  status: GroupedHistoryStatus;
  createdAt: string;
  validatedAt: string | null;
  actorName: string | null;
  actorEmail: string | null;
  changeReason: string | null;
  changes: GroupedHistoryChange[];
  /**
   * Present only for legacy pseudo-groups (status === 'legacy'). Raw audit.status
   * value so the UI can pick a badge ('synced', 'failed', 'cancelled', 'superseded',
   * 'observed'). Grouped entries leave this undefined; their `status` field drives
   * the badge instead.
   */
  auditStatus?: string;
}

const GROUPED_FIELD_IDS = ['NEWHUD.X1150', 'NEWHUD.X1151', '388', '4794', 'LW.LO_REDUCTION', '352'];
const LEGACY_HISTORY_FIELD_IDS = ['NEWHUD.X1150', 'NEWHUD.X1151', '388', '4794', 'LW.LO_REDUCTION', 'LW.CONCESSION', '352'];

/**
 * The retired auto-save sent each Investor Loan # entry twice, so its audit
 * holds same-value rows. They stay in the audit table; the panel hides them.
 */
function isUnchangedInvestorLoanNumber(a: { field_id: string; old_value: string | null; new_value: string | null }): boolean {
  return a.field_id === INVESTOR_LOAN_NUMBER_FIELD_ID && (a.old_value ?? '').trim() === (a.new_value ?? '').trim();
}

/**
 * Unified grouped + legacy pricing history for display.
 *
 * Real groups come from pricing_write_groups + joined encompass_write_audit.
 * Legacy audit rows (group_id IS NULL, pre-Phase-2 single-field path) are
 * normalized into one-change pseudo-groups so the panel renders one flat list.
 * Both kinds are sorted newest-first.
 *
 * Legacy-row status filter keeps only synced/observed history so audit rows
 * remain history, never a current-value overlay.
 */
export async function getPricingChangeHistoryGrouped(
  encompassLoanId: string,
  limit: number = 20
): Promise<GroupedHistoryEntry[]> {
  // 1. Fetch grouped rows (newest-first, capped).
  const { data: groupRows, error: groupErr } = await supabase
    .from('pricing_write_groups')
    .select('id, status, created_at, validated_at, change_reason, created_by, created_by_email')
    .eq('encompass_loan_id', encompassLoanId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (groupErr) {
    log.error('Failed to fetch pricing groups', { encompassLoanId }, groupErr);
    return [];
  }

  // 2. Audits for the grouped rows — single batched fetch by group_id IN (...).
  const groupIds = (groupRows ?? []).map((g) => g.id);
  type AuditRow = {
    group_id: string | null;
    field_id: string;
    field_label: string | null;
    old_value: string | null;
    new_value: string | null;
    status: string;
    change_reason: string | null;
    created_by: string | null;
    created_by_email: string | null;
    created_at: string;
    id: string;
  };
  let groupedAudits: AuditRow[] = [];
  if (groupIds.length > 0) {
    const { data, error } = await supabase
      .from('encompass_write_audit')
      .select('id, group_id, field_id, field_label, old_value, new_value, status, change_reason, created_by, created_by_email, created_at')
      .in('group_id', groupIds)
      .in('field_id', GROUPED_FIELD_IDS)
      .order('created_at', { ascending: true });
    if (error) {
      log.error('Failed to fetch grouped audits', { encompassLoanId }, error);
      return [];
    }
    groupedAudits = ((data ?? []) as AuditRow[]).filter((a) => !isUnchangedInvestorLoanNumber(a));
  }

  // 3. Legacy audits (pre-Phase-2 single-field writes). Keep them in history
  //    only; current Product & Pricing values come from encompass_loans.
  const { data: legacyAuditsRaw, error: legacyErr } = await supabase
    .from('encompass_write_audit')
    .select('id, group_id, field_id, field_label, old_value, new_value, status, change_reason, created_by, created_by_email, created_at')
    .eq('encompass_loan_id', encompassLoanId)
    .is('group_id', null)
    .in('field_id', LEGACY_HISTORY_FIELD_IDS)
    .in('status', ['synced', 'observed'])
    .order('created_at', { ascending: false })
    .limit(limit);
  if (legacyErr) {
    log.error('Failed to fetch legacy pricing audits', { encompassLoanId }, legacyErr);
    return [];
  }
  const legacyAudits = ((legacyAuditsRaw ?? []) as AuditRow[]).filter((a) => !isUnchangedInvestorLoanNumber(a));

  // 4. Resolve actor display names across all created_by UUIDs (groups + audits).
  const actorIds = new Set<string>();
  for (const g of groupRows ?? []) {
    if (g.created_by) {
actorIds.add(g.created_by);
}
  }
  for (const a of [...groupedAudits, ...legacyAudits]) {
    if (a.created_by) {
actorIds.add(a.created_by);
}
  }
  const userMap: Record<string, string> = {};
  if (actorIds.size > 0) {
    const { data: users } = await supabase
      .from('users')
      .select('id, first_name, last_name')
      .in('id', [...actorIds]);
    if (users) {
      for (const u of users) {
        const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
        if (name) {
userMap[u.id] = name;
}
      }
    }
  }

  // 5. Build grouped entries. Group-level fields (actor, email, changeReason)
  //    take precedence; fall back to any audit's value for resilience.
  const auditsByGroup = new Map<string, AuditRow[]>();
  for (const a of groupedAudits) {
    const key = a.group_id as string;
    const list = auditsByGroup.get(key) ?? [];
    list.push(a);
    auditsByGroup.set(key, list);
  }

  const groupedEntries: GroupedHistoryEntry[] = (groupRows ?? []).map((g) => {
    const audits = auditsByGroup.get(g.id) ?? [];
    const firstAuditWithActor = audits.find((a) => a.created_by || a.created_by_email);
    const firstAuditWithReason = audits.find((a) => a.change_reason);

    const actorId = g.created_by ?? firstAuditWithActor?.created_by ?? null;
    return {
      groupId: g.id,
      status: g.status as GroupedHistoryStatus,
      createdAt: g.created_at,
      validatedAt: g.validated_at ?? null,
      actorName: (actorId && userMap[actorId]) || null,
      actorEmail: g.created_by_email ?? firstAuditWithActor?.created_by_email ?? null,
      changeReason: g.change_reason ?? firstAuditWithReason?.change_reason ?? null,
      changes: audits.map((a) => ({
        fieldId: a.field_id,
        fieldLabel: resolvePricingFieldLabel(a.field_id, a.field_label),
        previousValue: formatPricingValue(a.old_value, a.field_id),
        newValue: formatPricingValue(a.new_value, a.field_id)
      }))
    };
  });

  // 6. Legacy pseudo-groups: one entry per audit row, single-field changes[].
  const legacyEntries: GroupedHistoryEntry[] = legacyAudits.map((a) => ({
    groupId: null,
    status: 'legacy',
    createdAt: a.created_at,
    validatedAt: null,
    actorName: (a.created_by && userMap[a.created_by]) || null,
    actorEmail: a.created_by_email || null,
    changeReason: a.change_reason || null,
    changes: [{
      fieldId: a.field_id,
      fieldLabel: resolvePricingFieldLabel(a.field_id, a.field_label),
      previousValue: formatPricingValue(a.old_value, a.field_id),
      newValue: formatPricingValue(a.new_value, a.field_id)
    }],
    auditStatus: a.status
  }));

  // 7. Merge + sort newest-first, cap to `limit`.
  return [...groupedEntries, ...legacyEntries]
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0))
    .slice(0, limit);
}
